// BillingScreen.js
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { auth } from "../../services/firebase";
import useRazorpayPayment from "../../hooks/useRazorpayPayment";
import useFetchCouponData from "../../hooks/useFetchCouponData";
import CustomAlertModal from "../../components/CustomAlertModal";
import "./BillingScreen.css";

export default function BillingScreen() {
    const location = useLocation();
    const navigate = useNavigate();
    const pack = location.state?.pack;

    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [coupon, setCoupon] = useState("");
    const [alertData, setAlertData] = useState(null);
    const [finalPrice, setFinalPrice] = useState(null);
    const [hasDiscount, setHasDiscount] = useState(false);
    const [discount, setDiscount] = useState(0);
    const [couponMessage, setCouponMessage] = useState("");

    const { initiatePayment } = useRazorpayPayment();
    const { applyCoupon } = useFetchCouponData();

    // ✅ Auto-fill name & phone from Google signed-in user
    useEffect(() => {
        const user = auth.currentUser;
        if (user?.displayName) setName(user.displayName);
        else if (user?.email) setName(user.email.split("@")[0]);
        if (user?.phoneNumber) setPhone(user.phoneNumber);
    }, []);

    // Set initial final price and discount
    useEffect(() => {
        setFinalPrice(pack.price);
        setHasDiscount(pack.mrp > pack.price);
    }, [pack]);

    if (!pack) {
        return (
            <div className="billing-page">
                <div className="billing-container">
                    <p className="error-text">No pack selected. Please go back and try again.</p>
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        ← Back
                    </button>
                </div>
            </div>
        );
    }


    const handleApplyCoupon = async () => {
        if (!coupon.trim()) {
            setCouponMessage("⚠️ Please enter a coupon code before applying.");
            return;
        }

        const discountedPrice = await applyCoupon(pack.price, coupon);
        setFinalPrice(discountedPrice);

        if (discountedPrice < pack.price) {
            setCouponMessage("🎉 Coupon applied successfully!");
        } else {
            setCouponMessage("❌ Invalid or expired coupon.");
        }

        const hasDiscount = pack.mrp > discountedPrice;
        setHasDiscount(hasDiscount);
        const discount = hasDiscount ? pack.mrp - discountedPrice : 0;
        setDiscount(discount);
    };


    // ✅ Payment success handler
    const handlePaymentSuccess = () => {
        setAlertData({
            type: "success",
            message: "✅ Payment successful! Redirecting to Interview Packs...",
        });
        setTimeout(() => navigate("/user/packs"), 2500);
    };

    // ❌ Payment failure handler
    const handlePaymentFailure = () => {
        setAlertData({
            type: "error",
            message: "❌ Payment failed or cancelled. Redirecting to Interview Packs...",
        });
        setTimeout(() => navigate("/user/packs"), 2500);
    };

    // 💳 Trigger payment
    const handlePayNow = () => {
        if (!name.trim() || !phone.trim()) {
            alert("Please enter your name and phone number.");
            return;
        }

        initiatePayment({
            price: finalPrice,
            pack,
            name,
            phone,
            coupon,
            onSuccess: handlePaymentSuccess,
            onFailure: handlePaymentFailure,
        });
    };

    return (
        <div className="billing-page">
            <div className="billing-container">
                <h2 className="billing-title">Billing & Payment</h2>

                {/* 🔹 Pack Details */}
                <div className="billing-card">
                    <h3>{pack.name}</h3>
                    <div className="price-section">
                        {hasDiscount && <span className="mrp-text">₹{pack.mrp}</span>}
                        <span className="final-price">₹{finalPrice}</span>
                    </div>

                    {discount > 0 && <p className="save-text">You save ₹{discount}!</p>}
                </div>

                {/* 🧾 Billing Form */}
                <div className="billing-form">
                    <label>Full Name</label>
                    <input
                        type="text"
                        value={name}
                        placeholder="Enter your name"
                        onChange={(e) => setName(e.target.value)}
                    />

                    <label>Phone Number</label>
                    <input
                        type="tel"
                        value={phone}
                        placeholder="Enter your phone number"
                        onChange={(e) => setPhone(e.target.value)}
                    />

                    {/* 💸 Show coupon offer if available */}
                    {pack?.coupon && (
                        <div className="offer-box">
                            <p className="offer-text">
                                💸 Offer Available: Use code{" "}
                                <strong>{pack.coupon}</strong> to get instant discount!
                            </p>
                        </div>
                    )}

                    <label>Coupon Code (optional)</label>

                    <div>
                        <input
                            type="text"
                            value={coupon}
                            placeholder="Enter coupon code"
                            onChange={(e) => setCoupon(e.target.value)}
                        />
                        {couponMessage && (
                            <p
                                style={{
                                    color: couponMessage.includes("successfully") ? "#16a34a" : "#b91c1c",
                                    fontWeight: "500",
                                    marginTop: "8px",
                                }}
                            >
                                {couponMessage}
                            </p>
                        )}

                        <button className="apply-coupon-btn" onClick={() => { handleApplyCoupon ()}}>
                            Apply Coupon
                        </button>
                    </div>

                    <div className="price-display">Final Price: ₹{finalPrice}</div>

                    <button className="pay-btn" onClick={handlePayNow}>
                        Pay ₹{finalPrice}
                    </button>
                    
                </div>
            </div>

            {/* 🎨 Custom Alert */}
            {alertData && (
                <CustomAlertModal
                    type={alertData.type}
                    message={alertData.message}
                    onClose={() => navigate("/user/packs")}
                />
            )}
        </div>
    );
}
